const fitmentText = (key, replacements = {}) => window.vehicleFitmentText?.(key, replacements) || key;

// Pure Storage Layer - No Side Effects
class VehicleFitmentStorage {
  constructor() {
    this.storageKey = 'vehicle_fitment_history';
    this.emptyData = { current: null, history: [] };
    this.memoryData = { ...this.emptyData };
  }

  // Pure getter - no side effects
  getData() {
    const stored = window.ThemeStorage?.getJson?.(this.storageKey, this.memoryData);
    const cleanData = this.enforceNoDuplicates(stored && typeof stored === 'object' ? stored : this.memoryData);
    this.memoryData = cleanData;
    return cleanData;
  }

  // Pure setter - no side effects, returns success/failure
  setData(data) {
    const cleanData = this.enforceNoDuplicates(data || this.emptyData);
    const result = window.ThemeStorage?.setJson?.(this.storageKey, cleanData) || { ok: false };
    this.memoryData = cleanData;
    return { success: true, persisted: Boolean(result.ok), data: cleanData, error: result.error };
  }

  // Pure validation - no side effects
  enforceNoDuplicates(data) {
    const cleaned = {
      current: this.enrichVehicle(data.current),
      history: (Array.isArray(data.history) ? data.history : [])
        .map((item) => this.enrichVehicle(item))
        .filter((item) => item && item.id)
    };

    // Rule 1: Remove current.id from history if it exists
    if (cleaned.current && cleaned.current.id) {
      cleaned.history = cleaned.history.filter(item => item.id !== cleaned.current.id);
    }

    // Rule 2: Remove duplicate IDs within history array (keep first occurrence)
    const seenIds = new Set();
    cleaned.history = cleaned.history.filter(item => {
      if (seenIds.has(item.id)) {
        return false;
      }
      seenIds.add(item.id);
      return true;
    });

    // Limit history size
    cleaned.history = cleaned.history.slice(0, 5);

    return cleaned;
  }

  enrichVehicle(vehicle) {
    if (!vehicle || !vehicle.id) {
      return null;
    }

    const enriched = { ...vehicle, id: String(vehicle.id) };
    const fitment = this.fitmentById(enriched.id);

    if (!fitment) {
      return enriched;
    }

    const engineName = fitment.engine?.name || 'Any';

    return {
      ...enriched,
      displayName: fitment.name || enriched.displayName,
      imageUrl: fitment.imageUrl || fitment.image || enriched.imageUrl,
      selectionPath: enriched.selectionPath || {
        fitmentId: fitment.id,
        makeGid: fitment.make?.gid || '',
        modelGid: fitment.model?.gid || '',
        yearGid: fitment.year?.gid || '',
        engineValue: String(fitment.engine?.value || engineName).trim().toLowerCase()
      }
    };
  }

  fitmentById(vehicleId) {
    if (!Array.isArray(window.vehicleFitmentIndex)) {
      return null;
    }

    return window.vehicleFitmentIndex.find((fitment) => String(fitment.id) === String(vehicleId)) || null;
  }

  // Set a vehicle as current, optionally moving old current to history
  setCurrent(vehicleId, metadata = {}, moveOldToHistory = true) {
    const data = this.getData();
    const timestamp = new Date().toISOString();
    
    // If there's an existing current vehicle that's different, handle it
    if (moveOldToHistory && data.current && data.current.id !== vehicleId) {
      // Preserve the old current's metadata when moving to history
      data.history.unshift({
        ...data.current,
        lastSeen: timestamp
      });
    }
    
    // Check if this vehicle exists in history to preserve its metadata
    const existingInHistory = data.history.find(item => item.id === vehicleId);
    
    // Set as current, preserving any existing metadata
    data.current = {
      id: vehicleId,
      firstSeen: existingInHistory?.firstSeen || timestamp,
      lastSeen: timestamp,
      displayName: metadata.displayName || existingInHistory?.displayName,
      imageUrl: metadata.imageUrl || existingInHistory?.imageUrl,
      ...metadata
    };
    
    // Remove from history if it exists there
    data.history = data.history.filter(item => item.id !== vehicleId);
    
    return this.setData(data);
  }

  // Move current to history (used when explicitly changing vehicles)
  moveCurrentToHistory() {
    const data = this.getData();
    
    if (!data.current) {
      return { success: true, data }; // Nothing to move
    }

    const timestamp = new Date().toISOString();
    
    // Update last seen and move to history
    const vehicleData = {
      ...data.current,
      lastSeen: timestamp
    };

    data.history.unshift(vehicleData);
    data.current = null;

    return this.setData(data);
  }

  clearCurrent({ moveToHistory = false } = {}) {
    if (moveToHistory) {
      return this.moveCurrentToHistory();
    }

    const data = this.getData();
    data.current = null;
    return this.setData(data);
  }

  removeFromHistory(vehicleId) {
    const data = this.getData();
    data.history = data.history.filter(item => String(item.id) !== String(vehicleId));
    return this.setData(data);
  }

  clear() {
    const data = { ...this.emptyData };
    const result = window.ThemeStorage?.remove?.(this.storageKey) || { ok: false };
    this.memoryData = data;
    return { success: true, persisted: Boolean(result.ok), data, error: result.error };
  }
}

// Metaobject Data Layer - Pure Data Fetching
class MetaobjectService {
  constructor() {
    this.cache = new Map();
  }

  async getMetaobject(id) {
    const indexed = this.findMetaobjectInFitmentIndex(id);
    if (indexed) return indexed;
    if (this.cache.has(id)) {
      return this.cache.get(id);
    }

    const metaobject = await this.fetchMetaobject(id);
    if (metaobject) {
      this.cache.set(id, metaobject);
    }
    return metaobject;
  }

  async fetchMetaobject(id) {
    try {
      const indexData = this.findMetaobjectInFitmentIndex(id);
      if (indexData) {
        return indexData;
      }

      if (window.vehicleFitmentIndexStatus?.loading && window.vehicleFitmentIndexReady?.then) {
        await window.vehicleFitmentIndexReady.catch(() => null);
        const loadedIndexData = this.findMetaobjectInFitmentIndex(id);
        if (loadedIndexData) {
          return loadedIndexData;
        }
      }

      const existingData = this.findMetaobjectInDOM(id);
      if (existingData) {
        return existingData;
      }
      return this.createPlaceholderMetaobject(id);
    } catch (error) {
      console.warn(`Failed to fetch metaobject ${id}:`, error);
      return this.createPlaceholderMetaobject(id);
    }
  }

  findMetaobjectInFitmentIndex(id) {
    if (!Array.isArray(window.vehicleFitmentIndex)) {
      return null;
    }

    const fitment = window.vehicleFitmentIndex.find((item) => String(item.id) === String(id));
    if (!fitment?.name) {
      return null;
    }

    const imageUrl = fitment.imageUrl || fitment.image || '';

    return {
      id: String(id),
      display_name: fitment.name,
      image: imageUrl ? { url: imageUrl } : null,
      fields: {
        display_name: fitment.name
      }
    };
  }

  findMetaobjectInDOM(id, doc = document) {
    const filterInputs = doc.querySelectorAll(`input[value*="${id}"]`);
    
    for (const input of filterInputs) {
      const label = input.closest('.checkbox-facet')?.querySelector('.checkbox-facet-label-text');
      const image = input.closest('.checkbox-facet')?.querySelector('.checkbox-facet-image');
      
      if (label) {
        return {
          id: id,
          display_name: label.textContent.trim(),
          image: image ? { url: image.src } : null,
          fields: {
            display_name: label.textContent.trim()
          }
        };
      }
    }
    return null;
  }

  createPlaceholderMetaobject(id) {
    return {
      id: id,
      display_name: `Vehicle Configuration ${id}`,
      image: null,
      fields: {
        display_name: `Vehicle Configuration ${id}`,
        last_accessed: new Date().toISOString()
      }
    };
  }
}

// UI Layer - Pure Rendering
class VehicleFitmentRenderer {
  constructor(metaobjectService) {
    this.metaobjectService = metaobjectService;
    this.isRendering = false;
    this.pendingRender = null;
    this.removeHandlers = new Map(); // Track event handlers for cleanup
    this.accordionHandlers = new Map(); // Track accordion handlers
  }

  // Debounced render - prevents concurrent renders
  async render(data) {
    if (this.isRendering) {
      this.pendingRender = data;
      return;
    }

    this.isRendering = true;

    try {
      await this.doRender(data);
      
      // Handle pending render if one was queued
      if (this.pendingRender) {
        const pendingData = this.pendingRender;
        this.pendingRender = null;
        await this.doRender(pendingData);
      }
    } finally {
      this.isRendering = false;
    }
  }


async doRender(data) {
  const fitmentHistoryElements = document.querySelectorAll('fitment-history');
  const fitmentCurrentElements = document.querySelectorAll('current-vehicle');
  this.cleanupEventHandlers();

  await Promise.all(
    Array.from(fitmentHistoryElements).map(async (fitmentHistoryElement) => {
      const historyItems = data.history.slice(0, 5);

      if (historyItems.length === 0) {
        fitmentHistoryElement.style.display = 'block';
        fitmentHistoryElement.innerHTML = `<span class="fitment-history-empty">${this.escapeHtml(fitmentText('no_vehicle_history'))}</span>`;
        return;
      }

      fitmentHistoryElement.style.display = 'block';
      const wasExpanded = fitmentHistoryElement
        .querySelector('.vehicle-history-toggle')
        ?.getAttribute('aria-expanded') === 'true';
      
      // Create accordion structure matching Shopify theme
      const accordionId = `vehicle-history-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      
      fitmentHistoryElement.innerHTML = `
        <button class="facet-toggle vehicle-history-toggle" type="button" aria-expanded="${wasExpanded ? 'true' : 'false'}" data-target="${accordionId}">
          <span class="vehicle-history-toggle__label">${this.escapeHtml(fitmentText('previous_vehicles'))}</span>
          <span class="angle angle-down">
            <span class="angle-icon"></span>
          </span>
        </button>
        <div id="${accordionId}" class="vehicle-history-container"${wasExpanded ? ' style="display: block;"' : ''}>
          <div class="fitment-history-items" data-history-items>
            <!-- History items will be inserted here -->
          </div>
          <div class="fitment-action-dock fitment-history-action-dock" data-history-action-dock aria-live="polite">
            <div class="fitment-action-dock__status" data-history-action-status>${this.escapeHtml(fitmentText('select_vehicle_to_continue'))}</div>
            <div class="fitment-action-dock__actions" data-history-action-actions hidden></div>
          </div>
        </div>
      `;

      const itemsContainer = fitmentHistoryElement.querySelector('[data-history-items]');
      itemsContainer?.setAttribute('role', 'radiogroup');
      itemsContainer?.setAttribute('aria-label', fitmentText('previous_vehicles'));
      const renderedItems = await Promise.all(
        historyItems.map(item => this.renderHistoryItem(item))
      );

      renderedItems.forEach(html => {
        if (html) {
          itemsContainer?.insertAdjacentHTML('beforeend', html);
        }
      });
    })
  );

  await Promise.all(
    Array.from(fitmentCurrentElements).map(async (fitmentCurrentElement) => {
      const item = data.current;

      // Handle null/empty current vehicle
      if (!item) {
        fitmentCurrentElement.innerHTML = '';
        return;
      }

      const currentProductIdString = fitmentCurrentElement.getAttribute("data-product-page-fitment-ids");

      const renderedItem = await this.renderCurrentItem(item, currentProductIdString)

      if (renderedItem) {
        fitmentCurrentElement.innerHTML = renderedItem;
      }

    })
  );

  this.attachEventListeners();
}

  async renderHistoryItem(item) {
    try {
      const metaobject = await this.metaobjectService.getMetaobject(item.id);
      
      // Update stored metadata if we have better info from DOM
      if (metaobject.display_name && metaobject.display_name !== `Vehicle Configuration ${item.id}`) {
        item.displayName = metaobject.display_name;
        if (metaobject.image?.url) {
          item.imageUrl = metaobject.image.url;
        }
      }
      
      return this.createHistoryItemHTML(item, metaobject);
    } catch (error) {
      console.warn(`Failed to render history item for ${item.id}:`, error);
      return null;
    }
  }

  async renderCurrentItem(item, currentProductIdString) {
    try {
      const metaobject = await this.metaobjectService.getMetaobject(item.id);
      
      // Update stored metadata if we have better info from DOM
      if (metaobject.display_name && metaobject.display_name !== `Vehicle Configuration ${item.id}`) {
        item.displayName = metaobject.display_name;
        if (metaobject.image?.url) {
          item.imageUrl = metaobject.image.url;
        }
      }
      
      return this.createCurrentItemHTML(item, metaobject, currentProductIdString);
    } catch (error) {
      console.warn(`Failed to render history item for ${item.id}:`, error);
      return null;
    }
  }

  createHistoryItemHTML(item, metaobject) {
    const gid = `gid://shopify/Metaobject/${item.id}`;
    const displayName = item.displayName || metaobject.display_name || metaobject.displayName;
    const imageUrl = item.imageUrl || metaobject.image?.url;
    const escapedDisplayName = this.escapeAttribute(displayName);
    const escapedImageUrl = this.escapeAttribute(imageUrl || '');
    
    const imageHTML = imageUrl ?
      `<img src="${escapedImageUrl}?width=140" alt="${escapedDisplayName}" />` :
      '<span class="fitment-state-card__placeholder" aria-hidden="true"></span>';

    return `
      <div class="fitment-history-item" data-id="${item.id}">
        <article class="fitment-state-card fitment-state-card--history">
          <button
            class="fitment-state-card__select js-history-fitment-card"
            type="button"
            role="radio"
            aria-checked="false"
            aria-label="${this.escapeAttribute(fitmentText('select_vehicle', { vehicle: displayName }))}"
            data-vehicle-id="${this.escapeAttribute(item.id)}"
            data-vehicle-gid="${this.escapeAttribute(gid)}"
            data-vehicle-label="${escapedDisplayName}"
            data-vehicle-image="${escapedImageUrl}"
          >
            <span class="fitment-state-card__media">
              ${imageHTML}
            </span>
            <span class="fitment-state-card__body">
              <span class="fitment-state-card__title">
                ${this.escapeHtml(displayName)}
              </span>
              <span class="fitment-state-card__caption">
                ${this.escapeHtml(fitmentText('last_used', { date: new Date(item.lastSeen || item.firstSeen).toLocaleDateString(document.documentElement.lang || undefined) }))}
              </span>
            </span>
          </button>
          <button
            class="fitment-state-card__remove facet-link-reset button-text-link js-history-remove"
            data-vehicle-id="${item.id}"
            type="button"
            aria-label="${this.escapeAttribute(fitmentText('remove_vehicle', { vehicle: displayName }))}"
          >
            <span aria-hidden="true">&times;</span>
          </button>
        </article>
      </div>
    `;
  }

  createCurrentItemHTML(item, metaobject, currentProductIdString) {
    const gid = `gid://shopify/Metaobject/${item.id}`;
    const removeUrl = this.buildRemoveUrl(gid);
    
    const displayName = item.displayName || metaobject.display_name || metaobject.displayName;
    const imageUrl = item.imageUrl || metaobject.image?.url;

    const productForm = document.querySelector('form[data-event-context="MainProductPage"]');
    let productFormId;
    if (productForm) {
      productFormId = productForm.getAttribute("id");
    }
    
    const imageHTML = imageUrl ?
      `<img src="${this.escapeAttribute(imageUrl)}?width=140" alt="${this.escapeAttribute(displayName)}" />` :
      '<span class="fitment-state-card__placeholder" aria-hidden="true"></span>';

    // Determine the status text based on currentProductIdString
    let statusText;
    let statusClass = 'fitment-state-card__status';
    let itemFits;
    if (currentProductIdString === null) {
        statusText = fitmentText('current_vehicle');
    } else {
        const productIds = currentProductIdString.split('|');
        itemFits = productIds.includes(item.id);
        statusText = itemFits ? fitmentText('fits_this_product') : fitmentText('does_not_fit_this_product');
        statusClass += itemFits ? ' fitment-state-card__status--fits' : ' fitment-state-card__status--no-fit';
    }
        
    const inputHTML = productForm && itemFits ? `
      <input
      style="display: none;"
      type="checkbox"
      id="fitment"
      name="properties[Fitment]"
      value="${this.escapeAttribute(displayName)}"
      form="${this.escapeAttribute(productFormId)}"
      autocomplete="off"
      checked
    >` : '';

    return `
    ${inputHTML}
    <div class="fitment-state-card-wrap">
      <div class="fitment-state-card__header">
        <facet-remove>
          <a
            href="${removeUrl}"
            class="fitment-state-card__change facet-link-reset button-text-link js-change-vehicle"
            data-vehicle-id="${item.id}"
            data-vehicle-label="${this.escapeAttribute(displayName)}"
            data-vehicle-image="${this.escapeAttribute(imageUrl || '')}"
          >
            <span class="fitment-state-card__change-label">${this.escapeHtml(fitmentText('change_vehicle'))}</span>
            <svg class="fitment-state-card__change-icon" aria-hidden="true" focusable="false" viewBox="0 0 24 24">
              <path d="M21 12a9 9 0 0 1-15.36 6.36L3 16"></path>
              <path d="M3 21v-5h5"></path>
              <path d="M3 12a9 9 0 0 1 15.36-6.36L21 8"></path>
              <path d="M21 3v5h-5"></path>
            </svg>
          </a>
        </facet-remove>
      </div>
      <article class="fitment-state-card fitment-state-card--current">
        <div class="fitment-state-card__media">
          ${imageHTML}
        </div>
        <div class="fitment-state-card__body">
          <span class="${statusClass}">${this.escapeHtml(statusText)}</span>
          <span class="fitment-state-card__title">${this.escapeHtml(displayName)}</span>
        </div>
        <facet-remove>
          <a
            href="${removeUrl}"
            class="fitment-state-card__remove facet-link-reset button-text-link js-change-vehicle"
            data-vehicle-id="${item.id}"
            data-vehicle-label="${this.escapeAttribute(displayName)}"
            data-vehicle-image="${this.escapeAttribute(imageUrl || '')}"
            aria-label="${this.escapeAttribute(fitmentText('change_vehicle'))}"
          >
            <span aria-hidden="true">&times;</span>
          </a>
        </facet-remove>
      </article>
      <a href="${this.buildApplyUrl(gid)}" class="button button-block fitment-state-card__kits-link">${this.escapeHtml(fitmentText('shop_compatible_kits'))}</a>
    </div>
    `;
  }

  buildApplyUrl(gid) {
    if (window.VehicleFitmentUrls?.buildFitmentCollectionUrl) {
      return window.VehicleFitmentUrls.buildFitmentCollectionUrl(gid);
    }

    const url = new URL(window.location);
    url.searchParams.set('filter.p.m.custom.vehicle_fitment', gid);
    url.searchParams.delete("page");
    return url.toString();
  }

  buildRemoveUrl(gid) {
    if (window.VehicleFitmentUrls?.buildRemoveFitmentUrl) {
      return window.VehicleFitmentUrls.buildRemoveFitmentUrl();
    }

    const url = new URL(window.location);
    url.searchParams.delete('filter.p.m.custom.vehicle_fitment', gid);
    url.searchParams.delete("page");
    return url.toString();
  }

  escapeHtml(value) {
    return String(value || '').replace(/[&<>"']/g, (char) => {
      return {
        '&': '&amp;',
        '<': '&lt;',
        '>': '&gt;',
        '"': '&quot;',
        "'": '&#039;',
      }[char];
    });
  }

  escapeAttribute(value) {
    return this.escapeHtml(value);
  }

  cleanupEventHandlers() {
    // Remove tracked event handlers
    this.removeHandlers.forEach((handler, element) => {
      element.removeEventListener('click', handler);
    });
    this.removeHandlers.clear();

    // Remove accordion handlers
    this.accordionHandlers.forEach((handler, element) => {
      element.removeEventListener('click', handler);
    });
    this.accordionHandlers.clear();
  }

  attachEventListeners() {
    // Accordion toggles - using Shopify theme pattern
    document.querySelectorAll('.vehicle-history-toggle').forEach(toggleButton => {
      const handler = (event) => {
        event.preventDefault();
        event.stopPropagation();
        
        const targetId = toggleButton.dataset.target;
        const content = document.getElementById(targetId);
        
        if (content) {
          // Toggle visibility and aria-expanded attribute
          const isExpanded = toggleButton.getAttribute('aria-expanded') === 'true';
          
          if (isExpanded) {
            // Collapse
            content.style.display = 'none';
            toggleButton.setAttribute('aria-expanded', 'false');
          } else {
            // Expand
            content.style.display = 'block';
            toggleButton.setAttribute('aria-expanded', 'true');
          }
        }
      };
      
      toggleButton.addEventListener('click', handler);
      this.accordionHandlers.set(toggleButton, handler);
    });
  }
}

// Controller Layer - Coordinates Storage and UI
class VehicleFitmentController {
  constructor() {
    this.storage = new VehicleFitmentStorage();
    this.metaobjectService = new MetaobjectService();
    this.renderer = new VehicleFitmentRenderer(this.metaobjectService);
    this.pendingSelectionKey = 'vehicle_fitment_pending_selection';
    this.activeFitmentActionLabel = fitmentText('active_vehicle');
    this.viewKitsActionLabel = fitmentText('view_kits');
    this.actionDockPrompt = fitmentText('select_vehicle_to_continue');
    this.pendingPrefillPath = null;

    this.initEventListeners();
    this.initProductGridObserver();
    this.initFitmentIndexRefresh();
  }

  // Set a vehicle as current (from URL or user action)
  async setCurrentVehicle(vehicleId, metadata = {}) {
    console.log(`Setting vehicle ${vehicleId} as current`);

    const data = this.storage.getData();
    const moveOldToHistory = data.current && data.current.id !== vehicleId;
    const enrichedMetadata = {
      ...this.metadataForFitment(vehicleId),
      ...this.consumePendingSelection(vehicleId),
      ...metadata
    };

    const result = this.storage.setCurrent(vehicleId, enrichedMetadata, moveOldToHistory);

    if (result.success) {
      await this.renderer.render(result.data);
      await this.syncCollectionFitmentDisplay(result.data);
      this.updateProductCardFitmentMarkers();
      window.refreshFitmentFormWraps?.();
      this.notifyVehicleChange(result.data, 'set');
    }

    return result;
  }

  async clearCurrentVehicle({
    moveToHistory = false,
    prefill = false,
    sourceElement = null,
    updateCollection = true,
  } = {}) {
    console.log('Clearing current vehicle selection');

    const currentVehicle = this.storage.getData().current;
    this.pendingPrefillPath = prefill ? this.prefillPathForVehicle(currentVehicle) : null;

    const result = this.storage.clearCurrent({ moveToHistory });

    if (result.success) {
      if (updateCollection && this.renderWithoutCurrentFilter(sourceElement)) {
        this.notifyVehicleChange(result.data, 'clear');
        return result;
      }

      await this.renderer.render(result.data);
      await this.syncCollectionFitmentDisplay(result.data);
      this.updateProductCardFitmentMarkers();
      if (prefill) {
        this.applyPendingFitmentPrefill();
      }
      this.notifyVehicleChange(result.data, 'clear');
    }

    return result;
  }

  // Handle "Change Vehicle" button - clears current selection
  async handleChangeVehicle(sourceElement = null) {
    console.log('Change vehicle requested - clearing current selection');
    return this.clearCurrentVehicle({
      moveToHistory: true,
      prefill: true,
      sourceElement
    });
  }

  async removeFromHistory(vehicleId) {
    console.log(`Removing vehicle ${vehicleId} from history`);
    const result = this.storage.removeFromHistory(vehicleId);
    
    if (result.success) {
      await this.renderer.render(result.data);
    }
    
    return result;
  }

  initFitmentIndexRefresh() {
    const refreshFromIndex = () => {
      const current = this.storage.getData().current;

      if (!current?.id) {
        this.refresh();
        return;
      }

      const metadata = this.metadataForFitment(current.id);
      if (metadata.displayName || metadata.imageUrl || metadata.selectionPath) {
        this.setCurrentVehicle(current.id, metadata);
        return;
      }

      this.refresh();
    };

    if (window.vehicleFitmentIndexStatus?.complete) {
      window.queueMicrotask(refreshFromIndex);
      return;
    }

    if (window.vehicleFitmentIndexReady?.then) {
      window.vehicleFitmentIndexReady.then(refreshFromIndex).catch(refreshFromIndex);
      return;
    }

    window.addEventListener('vehicle-fitment-index:ready', refreshFromIndex, { once: true });
  }

  async refresh() {
    const data = this.storage.getData();
    await this.renderer.render(data);
    await this.syncCollectionFitmentDisplay(data);
    this.updateProductCardFitmentMarkers();
    window.refreshFitmentFormWraps?.();
    this.applyPendingFitmentPrefill();
    this.notifyVehicleChange(data, 'refresh');
  }

  notifyVehicleChange(data = this.storage.getData(), reason = 'refresh') {
    document.dispatchEvent(new CustomEvent('vehicle-fitment:changed', {
      detail: {
        reason,
        current: data.current || null,
        history: data.history || []
      }
    }));
  }

  async syncFromSearchParams(
    searchParams,
    {
      clearWhenMissing = false,
      moveToHistory = false,
      prefill = false,
      updateCollection = true,
    } = {},
  ) {
    const urlCheck = checkVehicleFitmentPattern(searchParams);

    if (urlCheck.found && urlCheck.matches && urlCheck.id) {
      return this.setCurrentVehicle(urlCheck.id);
    }

    if (clearWhenMissing && this.storage.getData().current) {
      return this.clearCurrentVehicle({ moveToHistory, prefill, updateCollection });
    }

    return { success: true, data: this.storage.getData() };
  }

  metadataForFitment(vehicleId) {
    const fitment = this.fitmentById(vehicleId);
    if (!fitment) {
      return {};
    }

    return {
      displayName: fitment.name,
      imageUrl: fitment.imageUrl || fitment.image || '',
      selectionPath: this.pathForFitment(fitment)
    };
  }

  fitmentById(vehicleId) {
    if (!Array.isArray(window.vehicleFitmentIndex)) {
      return null;
    }

    return window.vehicleFitmentIndex.find((fitment) => String(fitment.id) === String(vehicleId)) || null;
  }

  pathForFitment(fitment) {
    if (!fitment) {
      return null;
    }

    const engineName = fitment.engine?.name || 'Any';

    return {
      fitmentId: fitment.id,
      makeGid: fitment.make?.gid || '',
      modelGid: fitment.model?.gid || '',
      yearGid: fitment.year?.gid || '',
      engineValue: String(fitment.engine?.value || engineName).trim().toLowerCase()
    };
  }

  consumePendingSelection(vehicleId) {
    const selectionPath = window.ThemeStorage?.getJson?.(this.pendingSelectionKey, null, 'sessionStorage');
    window.ThemeStorage?.remove?.(this.pendingSelectionKey, 'sessionStorage');

    if (!selectionPath || String(selectionPath.fitmentId) !== String(vehicleId)) {
      return {};
    }

    return { selectionPath };
  }

  prefillPathForVehicle(vehicle) {
    if (!vehicle) {
      return null;
    }

    if (vehicle.selectionPath) {
      return vehicle.selectionPath;
    }

    return this.pathForFitment(this.fitmentById(vehicle.id));
  }

  applyPendingFitmentPrefill() {
    const instances = this.fitmentPrefillTargets();

    if (!this.pendingPrefillPath || !instances.length) {
      return false;
    }

    let applied = false;
    instances.forEach((instance) => {
      if (
        instance.prefillFromFitmentPath?.(this.pendingPrefillPath) ||
        instance.fitmentFormElement?.prefillFromFitmentPath?.(this.pendingPrefillPath)
      ) {
        applied = true;
      }
    });

    if (applied) {
      this.pendingPrefillPath = null;
    }

    return applied;
  }

  selectHistoryVehicle(card) {
    const historyRoot = card.closest('fitment-history');
    if (!historyRoot) {
      return;
    }

    const selected = this.vehicleFromHistoryElement(card);
    this.syncHistoryCardSelection(historyRoot, card);
    this.renderHistoryActionDock(historyRoot, selected);
  }

  vehicleFromHistoryElement(element) {
    const vehicleId = String(element?.dataset?.vehicleId || '').trim();
    const metadata = vehicleId ? this.metadataForFitment(vehicleId) : {};
    const displayName = element?.dataset?.vehicleLabel || metadata.displayName || '';
    const imageUrl = element?.dataset?.vehicleImage || metadata.imageUrl || '';
    const gid = element?.dataset?.vehicleGid || (vehicleId ? `gid://shopify/Metaobject/${vehicleId}` : '');

    return {
      id: vehicleId,
      gid,
      displayName,
      imageUrl,
      selectionPath: metadata.selectionPath || this.prefillPathForVehicle({ id: vehicleId }),
    };
  }

  syncHistoryCardSelection(historyRoot, selectedCard = null) {
    const cards = Array.from(historyRoot.querySelectorAll('.js-history-fitment-card[data-vehicle-id]'));
    const selectedId = selectedCard?.dataset?.vehicleId || '';

    cards.forEach((card, index) => {
      const isSelected = Boolean(selectedId && card.dataset.vehicleId === selectedId);
      card.classList.toggle('is-selected', isSelected);
      card.setAttribute('aria-checked', isSelected ? 'true' : 'false');
      card.tabIndex = isSelected || (!selectedId && index === 0) ? 0 : -1;
      card.closest('.fitment-state-card')?.classList.toggle('is-selected', isSelected);
    });
  }

  renderHistoryActionDock(historyRoot, selected = null) {
    const dock = historyRoot.querySelector('[data-history-action-dock]');
    const status = dock?.querySelector('[data-history-action-status]');
    const actions = dock?.querySelector('[data-history-action-actions]');

    if (!dock || !status || !actions) {
      return;
    }

    actions.innerHTML = '';

    if (!selected?.id) {
      status.textContent = this.actionDockPrompt;
      actions.hidden = true;
      return;
    }

    status.textContent = `Selected: ${selected.displayName}`;
    actions.hidden = false;

    const context = this.historyActionContext(historyRoot);

    if (context === 'collection') {
      actions.append(this.createHistoryViewKitsAction(selected, true));
      return;
    }

    if (context === 'product') {
      actions.append(
        this.createHistoryActiveFitmentAction(selected, true),
        this.createHistoryViewKitsAction(selected, false),
      );
      return;
    }

    actions.append(
      this.createHistoryActiveFitmentAction(selected, false),
      this.createHistoryViewKitsAction(selected, true),
    );
  }

  createHistoryViewKitsAction(vehicle, isPrimary) {
    const link = document.createElement('a');
    link.className = this.historyActionClassName(isPrimary);
    link.href = this.buildApplyUrl(vehicle.gid);
    link.textContent = this.viewKitsActionLabel;
    link.dataset.vehicleId = vehicle.id;
    link.dataset.vehicleLabel = vehicle.displayName;
    link.dataset.vehicleImage = vehicle.imageUrl || '';
    link.classList.add('js-history-view-kits');
    return link;
  }

  createHistoryActiveFitmentAction(vehicle, isPrimary) {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = this.historyActionClassName(isPrimary);
    button.textContent = this.activeFitmentActionLabel;
    button.dataset.vehicleId = vehicle.id;
    button.dataset.vehicleLabel = vehicle.displayName;
    button.dataset.vehicleImage = vehicle.imageUrl || '';
    button.classList.add('js-history-set-active');
    return button;
  }

  historyActionClassName(isPrimary) {
    return `fitment-dock-action ${isPrimary ? 'fitment-dock-action--primary' : 'fitment-dock-action--secondary'}`;
  }

  historyActionContext(historyRoot) {
    const pathname = window.location.pathname || '';

    if (
      window.VehicleFitmentUrls?.isCollectionPath?.(pathname) ||
      /^\/collections\/[^/?#]+\/?$/.test(pathname)
    ) {
      return 'collection';
    }

    if (historyRoot.closest('.product-info-sidebar')) {
      return 'product';
    }

    return 'discovery';
  }

  buildApplyUrl(gid) {
    if (window.VehicleFitmentUrls?.buildFitmentCollectionUrl) {
      return window.VehicleFitmentUrls.buildFitmentCollectionUrl(gid);
    }

    const url = new URL(window.location);
    url.searchParams.set('filter.p.m.custom.vehicle_fitment', gid);
    url.searchParams.delete('page');
    return url.toString();
  }

  storePendingHistorySelection(vehicleId) {
    const selectionPath = this.prefillPathForVehicle({ id: vehicleId });
    if (!selectionPath) {
      return;
    }

    window.ThemeStorage?.setJson?.(
      this.pendingSelectionKey,
      selectionPath,
      'sessionStorage',
    );
  }

  async setHistoryVehicleActive(button) {
    const vehicle = this.vehicleFromHistoryElement(button);
    if (!vehicle.id) {
      return;
    }

    button.disabled = true;

    try {
      await this.setCurrentVehicle(vehicle.id, {
        displayName: vehicle.displayName,
        imageUrl: vehicle.imageUrl,
        selectionPath: vehicle.selectionPath,
      });
    } finally {
      button.disabled = false;
    }
  }

  onHistoryCardKeydown(event) {
    const navigationKeys = ['ArrowDown', 'ArrowRight', 'ArrowUp', 'ArrowLeft', 'Home', 'End'];
    if (!navigationKeys.includes(event.key)) {
      return;
    }

    const historyRoot = event.currentTarget.closest('fitment-history');
    const cards = Array.from(historyRoot?.querySelectorAll('.js-history-fitment-card[data-vehicle-id]') || []);
    if (!cards.length) {
      return;
    }

    const currentIndex = cards.indexOf(event.currentTarget);
    if (currentIndex < 0) {
      return;
    }

    event.preventDefault();
    let nextIndex = currentIndex;

    if (event.key === 'ArrowDown' || event.key === 'ArrowRight') {
      nextIndex = (currentIndex + 1) % cards.length;
    } else if (event.key === 'ArrowUp' || event.key === 'ArrowLeft') {
      nextIndex = (currentIndex - 1 + cards.length) % cards.length;
    } else if (event.key === 'Home') {
      nextIndex = 0;
    } else if (event.key === 'End') {
      nextIndex = cards.length - 1;
    }

    cards[nextIndex].focus();
    this.selectHistoryVehicle(cards[nextIndex]);
  }

  fitmentPrefillTargets() {
    const targets = [];

    if (!window.fitmentFormInstances) {
      return Array.from(document.querySelectorAll('fitment-form'));
    }

    if (window.fitmentFormInstances instanceof Map) {
      targets.push(...window.fitmentFormInstances.values());
    } else {
      targets.push(...window.fitmentFormInstances);
    }

    targets.push(...document.querySelectorAll('fitment-form'));
    return Array.from(new Set(targets));
  }

  renderWithoutCurrentFilter(sourceElement) {
    const targetUrl = sourceElement?.href ? new URL(sourceElement.href, window.location.origin) : new URL(window.location);
    targetUrl.searchParams.delete('filter.p.m.custom.vehicle_fitment');
    targetUrl.searchParams.delete('page');

    if (
      typeof FacetFiltersForm !== 'undefined' &&
      document.getElementById('ProductGridContainer')
    ) {
      FacetFiltersForm.renderPage(targetUrl.searchParams.toString(), null, true, false, false, {
        vehicleFitmentNoAutoApply: true,
      });
      return true;
    }

    window.history.replaceState({}, '', targetUrl.toString());
    return false;
  }

  // Apply current vehicle filter if no filter in URL
  applyCurrentVehicleFilter() {
    const data = this.storage.getData();
    if (!data.current) return false;
    
    const gid = `gid://shopify/Metaobject/${data.current.id}`;
    const url = new URL(window.location);
    url.searchParams.set('filter.p.m.custom.vehicle_fitment', gid);
    url.searchParams.delete("page");
    const urlParams = url.searchParams.toString();
    
    console.log(`Applying current vehicle filter: ${data.current.id}`);
    console.log(`URL params: ${urlParams}`);
    
    if (typeof FacetFiltersForm !== 'undefined') {
      FacetFiltersForm.renderPage(urlParams, null, true, false, true);
      return true;
    }
    
    return false;
  }

  // Event Handlers
  initEventListeners() {
    document.addEventListener('click', (event) => {
      const historyCard = event.target.closest('.js-history-fitment-card');
      if (historyCard) {
        event.preventDefault();
        this.selectHistoryVehicle(historyCard);
        return;
      }

      const historyViewKits = event.target.closest('.js-history-view-kits');
      if (historyViewKits) {
        this.storePendingHistorySelection(historyViewKits.dataset.vehicleId);
        return;
      }

      const historySetActive = event.target.closest('.js-history-set-active');
      if (historySetActive) {
        event.preventDefault();
        this.setHistoryVehicleActive(historySetActive);
        return;
      }

      const historyRemoveBtn = event.target.closest('.js-history-remove');
      if (historyRemoveBtn) {
        event.preventDefault();
        event.stopPropagation();
        this.removeFromHistory(historyRemoveBtn.dataset.vehicleId);
      }
    }, true);

    document.addEventListener('click', (event) => {
      const changeVehicleBtn = event.target.closest('.js-change-vehicle');
      if (changeVehicleBtn) {
        // Change vehicle button clears the current selection
        event.preventDefault();
        event.stopPropagation();
        this.handleChangeVehicle(changeVehicleBtn);
      }
    }, true);

    document.addEventListener('keydown', (event) => {
      const historyCard = event.target.closest('.js-history-fitment-card');
      if (historyCard) {
        this.onHistoryCardKeydown(event);
      }
    }, true);
  }

  // Initialize observer for product grid changes (AJAX updates)
  initProductGridObserver() {
    const productGrid = document.getElementById('product-grid');
    if (!productGrid) {
      // If grid doesn't exist yet, try again after DOM is ready
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => this.initProductGridObserver());
      }
      return;
    }

    // Flag to prevent observer from triggering itself
    this.isUpdatingFitmentMarkers = false;

    // Use MutationObserver to detect when product grid is updated via AJAX
    const observer = new MutationObserver((mutations) => {
      // Don't react to our own current-fitment marker changes
      if (this.isUpdatingFitmentMarkers) {
        return;
      }

      // Check if mutations include actual product card changes (not just markers)
      const hasRelevantChanges = mutations.some(mutation => {
        // Ignore changes to marker elements
        if (mutation.target.classList?.contains('fitment-id-current-marker')) {
          return false;
        }
        // Ignore if the added/removed nodes are only markers
        const nodes = [...mutation.addedNodes, ...mutation.removedNodes];
        const hasNonMarkerChanges = nodes.some(node => {
          return node.nodeType === Node.ELEMENT_NODE &&
                 !node.classList?.contains('fitment-id-current-marker') &&
                 !node.querySelector?.('.fitment-id-current-marker');
        });
        return hasNonMarkerChanges;
      });

      if (!hasRelevantChanges) {
        return;
      }

      console.log('Product grid changed, updating fitment markers...');

      // Debounce marker updates to avoid excessive calls
      if (this.productGridUpdateTimeout) {
        clearTimeout(this.productGridUpdateTimeout);
      }

      this.productGridUpdateTimeout = setTimeout(() => {
        this.updateProductCardFitmentMarkers();
      }, 100);
    });

    observer.observe(productGrid, {
      childList: true,
      subtree: true
    });

    console.log('Product grid observer initialized for fitment markers');
  }

  // Update product card current-fitment markers based on current vehicle
  updateProductCardFitmentMarkers() {
    // Set flag to prevent observer from reacting to our changes
    this.isUpdatingFitmentMarkers = true;

    try {
      const data = this.storage.getData();

      console.log('Updating product card fitment markers...', data.current ? `Current ID: ${data.current.id}` : 'No current vehicle');

      document.body.classList.toggle('has-current-vehicle-fitment', Boolean(data.current));

      // Remove all existing markers/current states first
      const existingMarkers = document.querySelectorAll('.fitment-id-current-marker');
      console.log(`Removing ${existingMarkers.length} existing fitment markers`);
      existingMarkers.forEach(el => el.remove());
      document.querySelectorAll('.product-card-fitment.is-current-fitment, .product-card-fits-summary.is-current-fitment').forEach(el => {
        el.classList.remove('is-current-fitment');
        el.removeAttribute('aria-current');
      });

      // If no current vehicle, we're done
      if (!data.current) {
        console.log('No current vehicle, skipping current-fitment markers');
        return;
      }

      const currentId = data.current.id;
      let addedCount = 0;

      // Find all product cards with fitment IDs
      document.querySelectorAll('[class*="fitment-id-"]').forEach(fitmentDiv => {
        // Extract ID from class like "fitment-id-12345"
        const classMatch = fitmentDiv.className.match(/fitment-id-(\d+)/);
        if (!classMatch) return;

        const fitmentId = classMatch[1];

        // Mark if this is the current vehicle
        if (fitmentId === currentId) {
          fitmentDiv.classList.add('is-current-fitment');
          fitmentDiv.setAttribute('aria-current', 'true');

          const productCard = fitmentDiv.closest('.card-product');
          const summary = productCard?.querySelector('.product-card-fits-summary');
          if (!summary) {
            return;
          }

          summary.classList.add('is-current-fitment');
          summary.setAttribute('aria-current', 'true');

          const markerTarget = summary.querySelector('.product-card-fits-summary__text') || summary.querySelector('strong') || summary;
          if (markerTarget && !markerTarget.querySelector('.fitment-id-current-marker')) {
            const marker = document.createElement('span');
            marker.className = 'fitment-id-current-marker';
            marker.setAttribute('aria-hidden', 'true');
            markerTarget.prepend(marker);
            addedCount++;
          }
        }
      });

      console.log(`Added ${addedCount} current-fitment markers for vehicle ID: ${currentId}`);
    } finally {
      // Reset flag after a short delay to allow DOM to settle
      setTimeout(() => {
        this.isUpdatingFitmentMarkers = false;
      }, 150);
    }
  }

  activeFitmentGid() {
    const urlCheck = checkVehicleFitmentPattern();
    if (!urlCheck.found || !urlCheck.matches || !urlCheck.id) {
      return null;
    }

    return urlCheck.value;
  }

  async syncCollectionFitmentDisplay(data = this.storage.getData()) {
    const activeGid = this.activeFitmentGid();

    if (!activeGid || !data.current) {
      document.querySelectorAll('.js-fitment-fallback-chip, .js-fitment-fallback-clear').forEach((element) => {
        element.remove();
      });
      return;
    }

    this.renderFallbackActiveFilterChip(data.current);
    this.renderFallbackCompatibilityPanel(data.current);
  }

  displayNameForCurrent(current) {
    const fitment = this.fitmentById(current.id);
    return fitment?.name || current.displayName || `Vehicle Configuration ${current.id}`;
  }

  imageUrlForCurrent(current) {
    const fitment = this.fitmentById(current.id);
    return current.imageUrl || fitment?.imageUrl || fitment?.image || '';
  }

  buildCurrentFitmentRemoveUrl() {
    const url = new URL(window.location);
    url.searchParams.delete('filter.p.m.custom.vehicle_fitment');
    url.searchParams.delete('page');
    return url.toString();
  }

  buildCurrentFitmentApplyUrl(gid) {
    if (window.VehicleFitmentUrls?.buildFitmentCollectionUrl) {
      return window.VehicleFitmentUrls.buildFitmentCollectionUrl(gid);
    }

    const url = new URL(window.location);
    url.searchParams.set('filter.p.m.custom.vehicle_fitment', gid);
    url.searchParams.delete('page');
    return url.toString();
  }

  buildClearFiltersUrl() {
    const url = new URL(window.location);
    url.search = '';
    return url.toString();
  }

  renderFallbackActiveFilterChip(current) {
    const container = document.querySelector('.collection-actions-filters');
    if (!container) {
      return;
    }

    const displayName = this.displayNameForCurrent(current);
    const fallbackChip = container.querySelector('.js-fitment-fallback-chip .collection-filter-button');
    if (fallbackChip) {
      fallbackChip.href = this.buildCurrentFitmentRemoveUrl();
      const label = fallbackChip.querySelector('span:first-child');
      if (label) {
        label.textContent = `Compatibility: ${displayName}`;
      }
      return;
    }

    const existingCompatibilityChip = Array.from(container.querySelectorAll('.collection-filter-button')).some((button) => {
      return button.textContent?.trim().startsWith('Compatibility:');
    });

    if (!existingCompatibilityChip && !container.querySelector('.js-fitment-fallback-chip')) {
      const template = document.createElement('template');
      template.innerHTML = `
        <facet-remove class="js-fitment-fallback-chip">
          <a href="${this.buildCurrentFitmentRemoveUrl()}" class="js-change-vehicle collection-filter-button button button-secondary">
            <span>Compatibility: ${this.escapeHtml(displayName)}</span>
            <span class="collection-filter-dismiss">&times;</span>
          </a>
        </facet-remove>
      `;
      container.prepend(template.content);
    }

    const existingClearFiltersAction = container.querySelector(
      'facet-remove:not(.js-fitment-fallback-clear) > a.button-text-link',
    );

    if (
      !existingClearFiltersAction &&
      !container.querySelector('.js-fitment-fallback-clear') &&
      container.children.length > 0
    ) {
      const template = document.createElement('template');
      template.innerHTML = `
        <facet-remove class="js-fitment-fallback-clear">
          <a href="${this.buildClearFiltersUrl()}" class="button-text-link">Clear filters</a>
        </facet-remove>
      `;
      container.append(template.content);
    }
  }

  renderFallbackCompatibilityPanel(current) {
    const panels = document.querySelectorAll('.compatibility-fitment-panel');
    if (!panels.length) {
      return;
    }

    const displayName = this.displayNameForCurrent(current);
    const imageUrl = this.imageUrlForCurrent(current);
    const removeUrl = this.buildCurrentFitmentRemoveUrl();
    const imageHTML = imageUrl ?
      `<img src="${this.escapeAttribute(imageUrl)}?width=140" alt="${this.escapeAttribute(displayName)}" />` :
      '<span class="fitment-state-card__placeholder" aria-hidden="true"></span>';

    panels.forEach((panel) => {
      const existingFallbackPanel = panel.querySelector('.js-fitment-fallback-panel');
      if (existingFallbackPanel) {
        existingFallbackPanel.querySelectorAll('.js-change-vehicle').forEach((link) => {
          link.href = removeUrl;
          link.dataset.vehicleId = current.id;
          link.dataset.vehicleLabel = displayName;
          link.dataset.vehicleImage = imageUrl;
        });

        const media = existingFallbackPanel.querySelector('.fitment-state-card__media');
        if (media) {
          media.innerHTML = imageHTML;
        }

        const title = existingFallbackPanel.querySelector('.fitment-state-card__title');
        if (title) {
          title.textContent = displayName;
        }

        const kitsLink = existingFallbackPanel.querySelector('.fitment-state-card__kits-link');
        if (kitsLink) {
          kitsLink.href = this.buildCurrentFitmentApplyUrl(`gid://shopify/Metaobject/${current.id}`);
        }

        return;
      }

      const serverRenderedCard = panel.querySelector('.fitment-state-card--current');
      if (serverRenderedCard) {
        serverRenderedCard.querySelectorAll('.js-change-vehicle').forEach((link) => {
          link.href = removeUrl;
          link.dataset.vehicleId = current.id;
          link.dataset.vehicleLabel = displayName;
          link.dataset.vehicleImage = imageUrl;
        });

        const media = serverRenderedCard.querySelector('.fitment-state-card__media');
        if (media) {
          media.innerHTML = imageHTML;
        }

        const title = serverRenderedCard.querySelector('.fitment-state-card__title');
        if (title) {
          title.textContent = displayName;
        }

        return;
      }

      panel.innerHTML = `
        <div class="fitment-state-card-wrap js-fitment-fallback-panel">
          <div class="fitment-state-card__header">
            <facet-remove>
              <a
                href="${removeUrl}"
                class="fitment-state-card__change facet-link-reset button-text-link js-change-vehicle"
                data-vehicle-id="${this.escapeAttribute(current.id)}"
                data-vehicle-label="${this.escapeAttribute(displayName)}"
                data-vehicle-image="${this.escapeAttribute(imageUrl)}"
              >
                <span class="fitment-state-card__change-label">Change vehicle</span>
                <svg class="fitment-state-card__change-icon" aria-hidden="true" focusable="false" viewBox="0 0 24 24">
                  <path d="M21 12a9 9 0 0 1-15.36 6.36L3 16"></path>
                  <path d="M3 21v-5h5"></path>
                  <path d="M3 12a9 9 0 0 1 15.36-6.36L21 8"></path>
                  <path d="M21 3v5h-5"></path>
                </svg>
              </a>
            </facet-remove>
          </div>
          <article class="fitment-state-card fitment-state-card--current">
            <div class="fitment-state-card__media">
              ${imageHTML}
            </div>
            <div class="fitment-state-card__body">
              <span class="fitment-state-card__status fitment-state-card__status--fits">Fits your vehicle</span>
              <span class="fitment-state-card__title">${this.escapeHtml(displayName)}</span>
            </div>
            <facet-remove>
              <a
                href="${removeUrl}"
                class="fitment-state-card__remove facet-link-reset button-text-link js-change-vehicle"
                data-vehicle-id="${this.escapeAttribute(current.id)}"
                data-vehicle-label="${this.escapeAttribute(displayName)}"
                data-vehicle-image="${this.escapeAttribute(imageUrl)}"
                aria-label="Change vehicle"
              >
                <span aria-hidden="true">&times;</span>
              </a>
            </facet-remove>
          </article>
          <a href="${this.buildCurrentFitmentApplyUrl(`gid://shopify/Metaobject/${current.id}`)}" class="button button-block fitment-state-card__kits-link">Shop compatible kits</a>
        </div>
      `;
    });
  }

  escapeHtml(value) {
    return String(value || '').replace(/[&<>"']/g, (char) => {
      return {
        '&': '&amp;',
        '<': '&lt;',
        '>': '&gt;',
        '"': '&quot;',
        "'": '&#039;'
      }[char];
    });
  }

  escapeAttribute(value) {
    return this.escapeHtml(value);
  }

  // Utility Methods
  getCurrentId() {
    const data = this.storage.getData();
    return data.current ? data.current.id : null;
  }

  getHistory() {
    return this.storage.getData();
  }

  clearHistory() {
    const result = this.storage.clear();
    if (result.success) {
      this.renderer.render(result.data);
    }
    return result;
  }

  cleanup() {
    const data = this.storage.getData();
    const result = this.storage.setData(data); // Triggers enforceNoDuplicates
    if (result.success) {
      this.renderer.render(result.data);
    }
    return result;
  }
}

// URL Parameter Detection (Pure Function)
function checkVehicleFitmentPattern(searchParams = window.location.search) {
  const urlParams = searchParams instanceof URLSearchParams
    ? searchParams
    : new URLSearchParams(searchParams);
  const targetParam = 'filter.p.m.custom.vehicle_fitment';
  const expectedPattern = /^gid:\/\/shopify\/Metaobject\/\d+$/;

  const paramValue = urlParams.get(targetParam);

  if (paramValue) {
    const matches = expectedPattern.test(paramValue);

    console.log('Vehicle fitment parameter found:', paramValue);
    console.log('Pattern matches:', matches);

    if (matches) {
      const idMatch = paramValue.match(/\/(\d+)$/);
      const id = idMatch ? idMatch[1] : null;

      console.log('Extracted vehicle ID:', id);

      return { found: true, matches: true, id: id, value: paramValue };
    } else {
      return { found: true, matches: false, value: paramValue };
    }
  } else {
    console.log('No vehicle fitment parameter in URL');
    return { found: false, matches: false, value: null };
  }
}

function isExplicitNoFitmentHistoryState() {
  const state = window.history?.state;

  if (!state?.vehicleFitmentNoAutoApply) {
    return false;
  }

  return !checkVehicleFitmentPattern().found;
}

// Initialize System
console.log('=== Initializing Vehicle Fitment System ===');
const vehicleFitmentController = new VehicleFitmentController();

// Cleanup duplicates on initialization
vehicleFitmentController.cleanup();

// Check current URL and update storage
const urlCheck = checkVehicleFitmentPattern();
const storedCurrentId = vehicleFitmentController.getCurrentId();

if (urlCheck.found && urlCheck.matches && urlCheck.id) {
  // URL has a vehicle filter
  console.log(`✅ Found vehicle in URL: ${urlCheck.id}`);
  
  // Check if it's different from stored current
  if (storedCurrentId && storedCurrentId !== urlCheck.id) {
    console.log(`Different from stored current (${storedCurrentId}), updating...`);
  }
  
  // Set as current (will move old current to history if different)
  vehicleFitmentController.setCurrentVehicle(urlCheck.id);
  
} else {
  // No vehicle filter in URL
  console.log('❌ No vehicle filter in URL');
  
  if (storedCurrentId) {
    if (isExplicitNoFitmentHistoryState()) {
      console.log('Preserving explicit no-fitment history state');
      vehicleFitmentController.clearCurrentVehicle({
        moveToHistory: false,
        prefill: false,
        updateCollection: false,
      });
    } else {
      // Apply stored current vehicle as filter
      console.log(`Applying stored current vehicle: ${storedCurrentId}`);
      const filterApplied = vehicleFitmentController.applyCurrentVehicleFilter();
      
      if (!filterApplied) {
        console.warn('Could not apply vehicle filter - FacetFiltersForm may not be available');
      }
    }
  } else {
    console.log('No stored current vehicle');
  }
  
  // Always render current state
  vehicleFitmentController.refresh();
}

// Initial product-card fitment marker update on page load
vehicleFitmentController.updateProductCardFitmentMarkers();

// Public API
window.vehicleFitmentUtils = {
  getCurrent: () => vehicleFitmentController.getCurrentId(),
  getHistory: () => vehicleFitmentController.getHistory(),
  clearHistory: () => vehicleFitmentController.clearHistory(),
  setCurrentVehicle: (id, displayName, imageUrl) =>
    vehicleFitmentController.setCurrentVehicle(id, { displayName, imageUrl }),
  clearCurrentVehicle: (options) => vehicleFitmentController.clearCurrentVehicle(options),
  syncFromSearchParams: (searchParams, options) =>
    vehicleFitmentController.syncFromSearchParams(searchParams, options),
  changeVehicle: () => vehicleFitmentController.handleChangeVehicle(),
  removeFromHistory: (id) => vehicleFitmentController.removeFromHistory(id),
  updateDisplay: () => vehicleFitmentController.refresh(),
  updateCheckmarks: () => vehicleFitmentController.updateProductCardFitmentMarkers(),
  updateFitmentMarkers: () => vehicleFitmentController.updateProductCardFitmentMarkers(),

  showSummary: () => {
    const data = vehicleFitmentController.getHistory();
    console.log('=== Vehicle Fitment Summary ===');
    console.log('Current:', data.current);
    console.log(`History: ${data.history.length} entries`);
    data.history.slice(0, 5).forEach((entry, i) => {
      console.log(`  ${i + 1}. ID: ${entry.id}, Name: ${entry.displayName || 'Unknown'} (Last: ${entry.lastSeen?.split('T')[0] || 'N/A'})`);
    });
  }
};

// Export controller for direct access if needed
window.vehicleFitmentController = vehicleFitmentController;

console.log('Vehicle Fitment System initialized. Use vehicleFitmentUtils.showSummary() to see current state.');
